import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { 
  Upload, 
  Calendar, 
  MapPin, 
  Users, 
  DollarSign, 
  Tag, 
  Save, 
  Eye,
  Plus,
  X,
  ImageIcon,
  AlertCircle
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../../utils/axios.config';
import { baseUrl } from '../../utils/url';

function AddRecentActivity() {
  const [isLoading, setIsLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');

  const {
    register,
    formState: { errors },
    handleSubmit,
    watch,
    reset,
    setValue,
  } = useForm({
    defaultValues: {
      status: 'published',
      activityDate: new Date().toISOString().split('T')[0],
      participants: 0,
      budget: 0,
    }
  });

  // Fetch categories
  const fetchCategories = async () => {
    try {
      const { data } = await api.get('activity-categories/get');
      if (data.success) {
        setCategories(data.data.categories);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // Handle image preview
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle tags
  const addTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      const newTags = [...tags, tagInput.trim()];
      setTags(newTags);
      setValue('tags', newTags);
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove) => {
    const newTags = tags.filter(tag => tag !== tagToRemove);
    setTags(newTags);
    setValue('tags', newTags);
  };

  const handleTagInputKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag();
    }
  };

  const onSubmit = async (data) => {
    if (!selectedFile) {
      toast.error('অনুগ্রহ করে একটি ছবি নির্বাচন করুন');
      return;
    }

    setIsLoading(true);

    const formData = new FormData();

    // Append image file
    formData.append('image', selectedFile);
    formData.append('title', data.title);
    formData.append('shortDescription', data.shortDescription);
    formData.append('longDescription', data.longDescription);
    formData.append('category', data.category);
    formData.append('activityDate', data.activityDate);
    formData.append('location', data.location || '');
    formData.append('participants', data.participants || 0);
    formData.append('budget', data.budget || 0);
    formData.append('status', data.status);
    formData.append('featured', data.featured || false);
    
    // Append tags as JSON string
    formData.append('tags', JSON.stringify(tags));

    try {
      const { data: responseData } = await api.post('recent-activities/create', formData);

      if (responseData.success) {
        toast.success('কার্যক্রম সফলভাবে যুক্ত হয়েছে');
        reset();
        setImagePreview(null);
        setSelectedFile(null);
        setTags([]);
      } else {
        toast.error(responseData.error);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">নতুন কার্যক্রম যুক্ত করুন</h1>
          <p className="text-gray-600 mt-1">
            সংগঠনের নতুন কার্যক্রমের বিস্তারিত তথ্য প্রদান করুন
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        {/* Main Information Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <ImageIcon className="w-5 h-5 mr-2 text-primary" />
            মূল তথ্য
          </h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Image Upload */}
            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                কার্যক্রমের ছবি *
              </label>
              
              <div className="relative">
                {imagePreview ? (
                  <div className="relative w-full h-48 rounded-lg overflow-hidden border-2 border-dashed border-gray-300">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setImagePreview(null);
                        setValue('file', null);
                      }}
                      className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <label 
                    htmlFor="file-upload"
                    className="flex flex-col items-center justify-center w-full h-48 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100"
                  >
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <Upload className="w-8 h-8 mb-4 text-gray-500" />
                      <p className="mb-2 text-sm text-gray-500">
                        <span className="font-semibold">ক্লিক করুন</span> অথবা ড্র্যাগ করে ছবি আপলোড করুন
                      </p>
                      <p className="text-xs text-gray-500">PNG, JPG বা GIF (সর্বোচ্চ 5MB)</p>
                    </div>
                  </label>
                )}
                <input
                  id="file-upload"
                  type="file"
                  className="hidden"
                  accept="image/*"
                  onChange={handleImageChange}
                />
              </div>
            </div>

            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                কার্যক্রমের নাম *
              </label>
              <input
                {...register('title', {
                  required: 'কার্যক্রমের নাম আবশ্যক',
                  maxLength: {
                    value: 100,
                    message: 'সর্বোচ্চ ১০০ অক্ষর হতে পারে',
                  },
                })}
                type="text"
                placeholder="কার্যক্রমের নাম লিখুন"
                className="input input-bordered w-full"
              />
              {errors.title && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle size={16} className="mr-1" />
                  {errors.title.message}
                </p>
              )}
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                বিভাগ *
              </label>
              <select
                {...register('category', { required: 'বিভাগ নির্বাচন আবশ্যক' })}
                className="select select-bordered w-full"
              >
                <option value="">বিভাগ নির্বাচন করুন</option>
                {categories.map(category => (
                  <option key={category._id} value={category._id}>
                    {category.name}
                  </option>
                ))}
              </select>
              {errors.category && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle size={16} className="mr-1" />
                  {errors.category.message}
                </p>
              )}
            </div>

            {/* Activity Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                কার্যক্রমের তারিখ *
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  {...register('activityDate', { required: 'তারিখ আবশ্যক' })}
                  type="date"
                  className="input input-bordered w-full pl-10"
                />
              </div>
              {errors.activityDate && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle size={16} className="mr-1" />
                  {errors.activityDate.message}
                </p>
              )}
            </div>

            {/* Location */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                স্থান
              </label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  {...register('location')}
                  type="text"
                  placeholder="কার্যক্রমের স্থান"
                  className="input input-bordered w-full pl-10"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Description Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Eye className="w-5 h-5 mr-2 text-primary" />
            বিবরণ
          </h2>
          
          <div className="space-y-4">
            {/* Short Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                সংক্ষিপ্ত বিবরণ *
              </label>
              <textarea
                {...register('shortDescription', {
                  required: 'সংক্ষিপ্ত বিবরণ আবশ্যক',
                  maxLength: {
                    value: 200,
                    message: 'সর্বোচ্চ ২০০ অক্ষর হতে পারে',
                  },
                })}
                rows={3}
                placeholder="কার্যক্রমের সংক্ষিপ্ত বিবরণ লিখুন"
                className="textarea textarea-bordered w-full"
              />
              <div className="flex justify-between mt-1">
                {errors.shortDescription ? (
                  <p className="text-sm text-red-600 flex items-center">
                    <AlertCircle size={16} className="mr-1" />
                    {errors.shortDescription.message}
                  </p>
                ) : (
                  <span></span>
                )}
                <span className="text-xs text-gray-500">
                  {watch('shortDescription')?.length || 0}/200
                </span>
              </div>
            </div>

            {/* Long Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                বিস্তারিত বিবরণ *
              </label>
              <textarea
                {...register('longDescription', {
                  required: 'বিস্তারিত বিবরণ আবশ্যক',
                })}
                rows={6}
                placeholder="কার্যক্রমের বিস্তারিত বিবরণ লিখুন"
                className="textarea textarea-bordered w-full"
              />
              {errors.longDescription && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle size={16} className="mr-1" />
                  {errors.longDescription.message}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Additional Information Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Tag className="w-5 h-5 mr-2 text-primary" />
            অতিরিক্ত তথ্য
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Participants */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                অংশগ্রহণকারী
              </label>
              <div className="relative">
                <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  {...register('participants', { min: 0 })}
                  type="number"
                  min="0"
                  placeholder="অংশগ্রহণকারীর সংখ্যা"
                  className="input input-bordered w-full pl-10"
                />
              </div>
            </div>

            {/* Budget */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                বাজেট (টাকা)
              </label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  {...register('budget', { min: 0 })}
                  type="number"
                  min="0"
                  placeholder="বাজেট পরিমাণ"
                  className="input input-bordered w-full pl-10"
                />
              </div>
            </div>

            {/* Tags */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ট্যাগ
              </label>
              <div className="space-y-2">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={handleTagInputKeyPress}
                    placeholder="ট্যাগ লিখুন এবং এন্টার চাপুন"
                    className="input input-bordered flex-1"
                  />
                  <button
                    type="button"
                    onClick={addTag}
                    className="btn btn-outline btn-primary"
                  >
                    <Plus size={16} />
                  </button>
                </div>
                
                {tags.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {tags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-primary/10 text-primary border border-primary/20"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => removeTag(tag)}
                          className="ml-2 hover:text-red-600"
                        >
                          <X size={14} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Settings Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Save className="w-5 h-5 mr-2 text-primary" />
            প্রকাশনা সেটিংস
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                স্ট্যাটাস
              </label>
              <select
                {...register('status')}
                className="select select-bordered w-full"
              >
                <option value="published">প্রকাশিত</option>
                <option value="draft">খসড়া</option>
                <option value="archived">সংরক্ষিত</option>
              </select>
            </div>

            {/* Featured */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                বিশেষত্ব
              </label>
              <div className="form-control">
                <label className="label cursor-pointer justify-start">
                  <input
                    {...register('featured')}
                    type="checkbox"
                    className="checkbox checkbox-primary mr-3"
                  />
                  <span className="label-text">বিশেষ কার্যক্রম হিসেবে চিহ্নিত করুন</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={() => {
              reset();
              setImagePreview(null);
              setSelectedFile(null);
              setTags([]);
            }}
            className="btn btn-outline"
            disabled={isLoading}
          >
            রিসেট করুন
          </button>
          
          <button
            type="submit"
            disabled={isLoading}
            className="btn btn-primary min-w-[150px]"
          >
            {isLoading ? (
              <>
                <span className="loading loading-spinner loading-sm mr-2"></span>
                যুক্ত করা হচ্ছে...
              </>
            ) : (
              <>
                <Save size={20} className="mr-2" />
                কার্যক্রম যুক্ত করুন
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

export default AddRecentActivity;